import React, {useEffect, useState} from 'react';
import {IconButton, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {clientDetailsQuery} from '@store/clients/details';
import {ManageContact, ManageOrganisationUnit} from '@store/common/organisation.model';
import {clientsUnitsQuery, clientsUnitsService} from '@store/clients/units';

import {ArrowBack} from '@icons/index';

import OperationalUnitForm from '@components/unitDetails/OperationalUnit.form';
import OptionWithAlertComponent from '@components/moreOptions/OptionWithAlert.component';
import ManageContactDrawer from '@components/unitDetails/ManageContact.drawer';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import ManageBillingMethodDrawer from '@components/unitDetails/ManageBillingMethod.drawer';
import {useObservable} from '@ngneat/react-rxjs';
import {clientsUnitsDetailsQuery} from '@store/clients/units/details';
import {GlobalStatus} from '@store/common/globalStatus.enum';

interface ManageClientUnitScreenProps {
  isCreation?: boolean;
}

const ManageClientUnitScreen = (props: ManageClientUnitScreenProps) => {
  const {isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {clientId, clientUnitId} = useParams();

  const [{headOffice}] = useObservable(clientsUnitsDetailsQuery.clientUnitDetails$);
  const [loading, setLoading] = useState(false);

  const [openContact, setOpenContact] = useState<ManageContact | boolean>(false);
  const [openFacturationMode, setOpenFacturationMode] = useState<FacturationMode | undefined>();

  const [clientUnit, setClientUnit] = useState<ManageOrganisationUnit | undefined>(undefined);

  const [{loading: loadingFacturationMode}] = useObservable(clientsUnitsQuery.putClientUnitFacturationMode$);

  useEffect(() => {
    setClientUnit(headOffice);
  }, [headOffice]);


  const handleClose = () => {
    navigate(-1);
  };

  const handleSave = (clientUnit) => {
    if (isCreation) {
      if (!clientId) return;
      setLoading(true);
      clientsUnitsService.createClientUnit(clientId, clientUnit).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (cu) => {
            enqueueSnackbar(t('unitDetails.success.operationalUnit.create'), {variant: 'success'});
            navigate(`../${cu.id}`, {replace: true});
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!clientUnitId) return;
      setLoading(true);
      clientsUnitsService.updateClientUnitById(clientUnitId, clientUnit).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: (cu) => {
            enqueueSnackbar(t('unitDetails.success.operationalUnit.edit'), {variant: 'success'});
            setClientUnit(cu);
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const handleDeleteContact = (contactId, successCallback) => {
    if (!clientUnitId || !contactId) return;
    setLoading(true);
    clientsUnitsService.deleteClientsUnitContact(clientUnitId, contactId).pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (cu) => {
          enqueueSnackbar(t('delete.success.contact'), {variant: 'success'});
          setClientUnit(cu);
          successCallback();
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
  };

  const isArchived = clientDetailsQuery.isArchived || clientUnit?.status === GlobalStatus.ARCHIVED;

  return (
    <>
      <Stack flex={1} width="100%" pt="34px" px="24px" spacing={1} overflow="hidden">
        {isCreation && (
          <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={1} ml="-9px">
            <IconButton onClick={handleClose}>
              <ArrowBack/>
            </IconButton>
            <Typography fontSize="30px" fontWeight="700">
              {isCreation ? t('clients.details.clientUnits.newClientUnit') : clientUnit?.name || '-'}
            </Typography>
          </Stack>
        )}
        <OperationalUnitForm
          readOnly={isArchived}
          handleCancel={handleClose}
          handleSave={handleSave}
          handleManageContact={setOpenContact}
          handleFacturationMode={setOpenFacturationMode}
          handleDeleteContact={handleDeleteContact}
          moreOptions={[
            <OptionWithAlertComponent
              key="manage status"
              alertTitle={t(`${isArchived ? 'unarchive' : 'archive'}.title`)}
              alertDescription={t(`${isArchived ? 'unarchive' : 'archive'}.description.clientUnit`)}
              validateTitle={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}
              handleSuccess={() => {
                enqueueSnackbar(t(`${isArchived ? 'unarchive' : 'archive'}.success.clientUnit`), {variant: 'success'});
                setClientUnit((state) => ({
                  ...state,
                  status: isArchived ? GlobalStatus.ACTIVE : GlobalStatus.ARCHIVED,
                }));
              }}
              handleValidate={clientUnitId
                ? () => clientsUnitsService.updateClientsUnitsStatus(clientUnitId, isArchived ? GlobalStatus.ACTIVE : GlobalStatus.ARCHIVED)
                : undefined}
              label={t(`global.${isArchived ? 'unarchive' : 'archive'}`)}/>,
            <OptionWithAlertComponent
              key="delete"
              alertTitle={t('delete.title')}
              alertDescription={t('delete.description.clientUnit')}
              validateTitle={t('global.delete')}
              handleSuccess={() => {
                enqueueSnackbar(t('delete.success.clientUnit'), {variant: 'success'});
                handleClose();
              }}
              handleValidate={clientUnitId ? () => clientsUnitsService.deleteClientsUnit(clientUnitId) : undefined}
              label={t('global.delete')}/>
          ]}
          unit={clientUnit}
          loading={loading}/>
      </Stack>
      {!!openContact && (
        <ManageContactDrawer
          contact={typeof openContact !== 'boolean' ? openContact : undefined}
          readOnly={isArchived}
          handleClose={() => setOpenContact(false)}
          handleAdd={(contact) => {
            if (!clientUnitId) return;

            if (typeof openContact === 'boolean') {
              clientsUnitsService.createClientsUnitContact(clientUnitId, contact).subscribe({
                next: (cu) => {
                  enqueueSnackbar(t('unitDetails.success.contact.create'), {variant: 'success'});
                  setOpenContact(false);
                  setClientUnit(cu);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            } else {
              if (!contact.id) return;
              clientsUnitsService.updateClientsUnitContact(clientUnitId, contact.id, contact).subscribe({
                next: (cu) => {
                  enqueueSnackbar(t('unitDetails.success.contact.edit'), {variant: 'success'});
                  setClientUnit(cu);
                },
                error: (err) => enqueueSnackbar(err.text, err.options),
              });
            }
          }}/>
      )}
      {!!openFacturationMode && clientUnit?.facturationMode && (
        <ManageBillingMethodDrawer
          handleAdd={(updatedData) => {
            if (!clientUnitId || !clientUnit.facturationMode) return;
            clientsUnitsService.updateClientUnitFacturationMode(clientUnitId, clientUnit.facturationMode.id, clientUnit.facturationMode, updatedData).subscribe({
              next: () => {
                setClientUnit({...clientUnit, facturationMode: updatedData});
                enqueueSnackbar(t('unitDetails.billingMethod.success'), {variant: 'success'});
                setOpenFacturationMode(undefined);
              },
              error: (err) => enqueueSnackbar(err.text, err.options),
            });
          }}
          handleClose={() => setOpenFacturationMode(undefined)}
          facturationMode={clientUnit.facturationMode}
          loading={loadingFacturationMode}
          fromOrganisationUnit={true}
        />
      )}
    </>
  );
};

export default ManageClientUnitScreen;
